import { cn } from '@/lib/utils';
import { Input } from '@/components/ui/input';
import { MilestoneActions } from './milestone-actions';
import type { MilestoneItem } from '@/types';
import React from 'react';


export function MilestoneSelector({
  milestones,
  selectedKey,
  onSelect,
  loading,
  onEditMilestone,
  onDuplicateMilestone,
  isExternal,
}: {
  milestones: MilestoneItem[];
  selectedKey: string | null;
  onSelect: (k: string) => void;
  loading?: boolean;
  onEditMilestone?: (milestoneId: number) => void;
  onDuplicateMilestone?: (milestoneId: number) => void;
  isExternal?: boolean;
}) {
  const [q, setQ] = React.useState('');

  const filtered = React.useMemo(() => {
    const query = q.trim().toLowerCase();
    if (!query) return milestones;
    return milestones.filter((m) => m.title.toLowerCase().includes(query));
  }, [milestones, q]);

  if (loading) {
    return (
      <div className="space-y-3">
        <div className="h-10 rounded-2xl bg-muted animate-pulse" />
        <div className="h-16 rounded-3xl bg-muted animate-pulse" />
        <div className="h-16 rounded-3xl bg-muted animate-pulse" />
        <div className="h-16 rounded-3xl bg-muted animate-pulse" />
      </div>
    );
  }

  if (!milestones?.length) {
    return (
      <div className="rounded-3xl border border-dashed p-6 text-center">
        <div className="text-sm font-medium">No milestones yet</div>
        <div className="text-xs text-muted-foreground mt-1">
          Create milestones to start tracking work.
        </div>
      </div>
    );
  }

  return (
    <div className="border-none overflow-hidden">
      {/* Sticky header (enterprise feel) */}
      <div>
        <div className="p-3">
          <div>
            <Input
              value={q}
              onChange={(e) => setQ(e.target.value)}
              placeholder="Search milestones…"
            />
          </div>
        </div>
      </div>

      {/* List */}
      <div className="max-h-[520px] overflow-auto p-2">
        {filtered.length === 0 ? (
          <div className="rounded-3xl border border-dashed p-6 text-center">
            <div className="text-sm font-medium">No match</div>
            <div className="text-xs text-muted-foreground mt-1">
              Try a different keyword.
            </div>
          </div>
        ) : (
          <div className="space-y-2">
            {filtered.map((m) => {
              const selected = m.key === selectedKey;

              return (
                <div
                  key={m.key}
                  role="button"
                  tabIndex={0}
                  onClick={() => onSelect(m.key)}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' || e.key === ' ') onSelect(m.key);
                  }}
                  className={cn(
                    'group relative border rounded-[1.25rem] transition-all duration-300 cursor-pointer overflow-hidden',
                    'hover:shadow-md hover:border-primary/30 h-[100px] flex flex-col',
                    selected
                      ? 'border-primary/40 bg-primary/5 ring-1 ring-primary/10 shadow-lg shadow-primary/5'
                      : 'border-border/40 bg-background/40 backdrop-blur-sm'
                  )}
                >
                  <div className="p-4 flex-1 flex flex-col justify-between">
                    <div className="space-y-1">
                      <div className="flex items-start justify-between gap-3">
                        <div className="min-w-0 flex-1">
                          <div className="space-y-0.5">
                            <div className="flex items-center gap-2">
                              <h4
                                className={cn(
                                  'text-sm font-semibold tracking-tight transition-colors line-clamp-1',
                                  selected ? 'text-primary' : 'text-foreground'
                                )}
                              >
                                {m.title}
                              </h4>
                              <div className="px-1.5 py-0.5 rounded-md bg-muted/50 text-[10px] font-bold text-muted-foreground">
                                {m.progressPct}%
                              </div>
                            </div>
                            {/* {snippet && (
                              <p className="text-[11px] text-muted-foreground/70 line-clamp-1 leading-relaxed">
                                {snippet}
                              </p>
                            )} */}
                          </div>
                        </div>

                        <div
                          className={cn(
                            'shrink-0 opacity-0 group-hover:opacity-100 transition-opacity'
                          )}
                          onClick={(e) => e.stopPropagation()}
                        >
                          <MilestoneActions
                            milestoneId={m.milestoneId}
                            milestoneTitle={m.title}
                            onEdit={() => onEditMilestone?.(m.milestoneId)}
                            onDuplicate={() =>
                              onDuplicateMilestone?.(m.milestoneId)
                            }
                            isExternal={isExternal}
                          />
                        </div>
                      </div>

                      <div className="space-y-2">
                        {/* Progress Bar - Professional Thin */}
                        <div className="h-1 w-full bg-muted/60 rounded-full overflow-hidden">
                          <div
                            className="h-full bg-primary transition-all duration-700 ease-out"
                            style={{ width: `${m.progressPct}%` }}
                          />
                        </div>

                        {/* Status Counts - Enterprise Style */}
                        <div className="flex items-center gap-3">
                          <div className="flex items-center gap-1 text-[10px] font-medium text-emerald-600 dark:text-emerald-400/80">
                            <div className="h-1.5 w-1.5 rounded-full bg-current" />
                            <span>{m.counts.done} Done</span>
                          </div>
                          <div className="flex items-center gap-1 text-[10px] font-medium text-sky-600 dark:text-sky-400/80">
                            <div className="h-1.5 w-1.5 rounded-full bg-current" />
                            <span>{m.counts.doing} Doing</span>
                          </div>
                          <div className="h-1 w-1 rounded-full bg-border" />
                          <div className="text-[10px] font-medium text-muted-foreground/60">
                            {m.counts.todo} to do
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
}
